import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparklesIcon, CarIcon, ShieldCheckIcon, PaintbrushIcon, WrenchIcon } from 'lucide-react';
type ServiceItem = {
  title: string;
  description: string;
  price: string;
  link: string;
  icon: React.ReactNode;
};
const serviceItems: ServiceItem[] = [{
  title: 'Interior Detail',
  description: 'Deep clean, shampoo, and steam sanitize every surface inside your vehicle. Serving Camarillo, Thousand Oaks, and all of Ventura County with premium interior detailing at your door.',
  price: 'Starting at $180',
  link: '/services/interior-detailing',
  icon: <SparklesIcon className="w-7 h-7" data-id="element-407" data-mp="2tfxvfzwuuclt" />
}, {
  title: 'Exterior Detail',
  description: 'Hand wash, clay bar treatment, and paint protection for a showroom-quality finish. Professional mobile exterior detailing throughout Ventura County.',
  price: 'Starting at $160',
  link: '/services/exterior-detailing',
  icon: <CarIcon className="w-7 h-7" data-id="element-408" data-mp="1my0yty4lkrn3" />
}, {
  title: 'Engine Bay Detailing',
  description: 'Safe degreasing and dressing of the engine bay, performed on-site with care for your vehicle\u2019s electrical components.',
  price: 'From $120',
  link: '/services/engine-bay-detailing',
  icon: <ShieldCheckIcon className="w-7 h-7" data-id="element-409" data-mp="3ah3vr82o3lto" />
}, {
  title: 'Hybrid Ceramic Spray',
  description: 'Sprayed-on hybrid ceramic protection that adds gloss and helps your paint shed water and dirt. Applied at your home or office anywhere in Ventura County.',
  price: 'Call for Quote',
  link: '/services/hybrid-ceramic-spray',
  icon: <PaintbrushIcon className="w-7 h-7" data-id="element-410" data-mp="3fcszzopmtqwb" />
}, {
  title: 'Headlight Restoration',
  description: 'Restore cloudy, yellowed headlights to crystal-clear condition for improved visibility and appearance. Mobile headlight restoration serving Camarillo, Oxnard, and all of Ventura County.',
  price: 'From $80',
  link: '/services/headlight-restoration',
  icon: <WrenchIcon className="w-7 h-7" data-id="element-411" data-mp="2124p7zorh4l" />
}];
export function ServiceGrid() {
  return <section className="w-full bg-black py-16 px-4 border-t border-zinc-900" id="service-details" data-id="element-412" data-mp="2vsia02whex4z">
      <div className="max-w-6xl mx-auto" data-id="element-413" data-mp="2zsj04ksy0wrv">
        <div className="text-center mb-12" data-id="element-414" data-mp="2ztkaen24xf8j">
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-black uppercase mb-4" data-id="element-415" data-mp="34rvlt282vxsv">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-416" data-mp="39j3xy60slx9q">
              Specialty Detailing Services
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-417" data-mp="3jh9yokav4xd0" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mt-4" data-id="element-418" data-mp="3nj4ytlh254p7">
            Focused treatments for specific needs, delivered to your driveway in Ventura County
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-419" data-mp="1t53zkzhlifol">
          {serviceItems.map((service, index) => <motion.div key={index} initial={{
          y: 40,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className="group relative flex flex-col bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden hover:border-red-900/60 transition-all duration-300" data-id="element-420" data-mp="1y35hhh3ac8yh" data-mpi={index}>
              {/* Card Header */}
              <div className="p-6 pb-4" data-id="element-421" data-mp="29j1rikjuyrrw">
                <div className="flex items-center justify-between mb-3" data-id="element-422" data-mp="29hz2yja0d1f5">
                  <div className="p-2.5 bg-red-600/10 border border-red-900/30 rounded-lg text-red-500" data-id="element-423" data-mp="24jp2pu9ffamo">
                    {service.icon}
                  </div>
                  <span className="inline-flex items-center px-3 py-1 bg-zinc-800 border border-zinc-700 rounded-full text-sm font-bold text-yellow-400 tracking-wide" data-id="element-424" data-mp="1zmyqji0qsz6j">
                    {service.price}
                  </span>
                </div>
                <h3 className="text-2xl font-black text-white uppercase tracking-wide mt-2" data-id="element-425" data-mp="k3ao8bdy37dy">
                  {service.title}
                </h3>
              </div>

              {/* Description */}
              <div className="px-6 pb-4 flex-1" data-id="element-426" data-mp="2cstih9aubpfc">
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-427" data-mp="2ctl2dmv2ryps">
                  {service.description}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="p-6 pt-2 flex flex-col gap-3" data-id="element-428" data-mp="22zvyeyq79sem">
                <Link to={service.link} className="w-full text-center py-3 px-4 border border-zinc-700 text-white font-bold text-sm uppercase tracking-wider rounded-lg hover:bg-zinc-800 hover:border-zinc-600 transition-all" data-id="element-429" data-mp="2qskux5lpkxr8">
                  Learn More
                </Link>
                <Link to="/contact" className="w-full text-center py-3 px-4 bg-red-600 hover:bg-red-500 text-white font-bold text-sm uppercase tracking-wider rounded-lg transition-colors" data-id="element-430" data-mp="2lrx3iitkiewh">
                  Get a Quote
                </Link>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}